import React from 'react';
import { string, bool, array, func, shape, number, oneOf } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import {
  Avatar,
  Form,
  PrimaryButton,
  FieldMessageComposer,
  MultiPurposeInfo,
  NamedLink,
  PackagePrice,
  PricingModelBadge,
} from '../../components';
import { PRICING_MODELS } from '../../util/listingPackages';
import { hasBlockingAttachments } from '../../ducks/messageAttachments.duck';
import * as validators from '../../util/validators';
import { propTypes } from '../../util/types';
import { CiWarning } from 'react-icons/ci';

import css from './EnquiryForm.css';

const AT_RE = /@/i;
//eslint-disable-next-line
const PH_NO_RE = /\s?(\+\d{1,3}\s?)?1?\-?\.?\s?\(?\d{1,3}\)?[\s.-]?\d{3}[\s.-]?\d{4,5}/;
const PH_NO_RE2 = /(\+\d{1,3}\s?)?((\(\d{3}\)\s?)|(\d{3})(\s|-?))(\d{3}(\s|-?))(\d{4})(\s?(([E|e]xt[:|.|]?)|x|X)(\s?\d+))?/;

// True when the message carries no email, link or phone number. Detected
// contact details only show a warning; they never block sending.
const isMessageValid = value => {
  if (!value || value === '') {
    return false;
  }
  if (AT_RE.test(value)) {
    return false;
  }
  if (validators.hasLink(value)) {
    return false;
  }
  if (PH_NO_RE.test(value)) {
    return false;
  }
  if (PH_NO_RE2.test(value)) {
    return false;
  }
  return true;
};

// First-contact composer shown in the "start a conversation" modal on the
// profile and listing pages (and the new-enquiry modal in the transaction
// panel). Attachments are opt-in: the field shows the attach control only
// when both staging handlers are given.
const EnquiryFormComponent = props => {
  const { stagedAttachments, onUploadAttachment, onRemoveAttachment } = props;
  const showAttachments = !!onUploadAttachment && !!onRemoveAttachment;
  const attachments = stagedAttachments || [];

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          intl,
          listingTitle,
          authorDisplayName,
          author,
          enquiryPackage,
          sendEnquiryError,
          currentUser,
          values,
        } = fieldRenderProps;

        const classes = classNames(rootClassName || css.root, className);
        const fieldId = formId ? `${formId}.message` : 'message';

        const message = (values && values.message) || '';
        const trimmedMessage = message.trim();
        const showContactWarning = !!trimmedMessage && !isMessageValid(message);

        // A non-empty message is required, enforced by disabling submit rather
        // than a blur-time field error (which would shift the layout under the
        // attach control). In-flight uploads must finish, and failed ones be
        // removed, before the message can go out with its files.
        const submitDisabled =
          inProgress || !trimmedMessage || (showAttachments && hasBlockingAttachments(attachments));

        const canEnquire =
          currentUser?.profileImage !== null && currentUser?.attributes.profile.bio !== null;

        const messagePlaceholder = intl.formatMessage(
          { id: 'EnquiryForm.messagePlaceholder' },
          { authorDisplayName }
        );
        const contactInformationErrorMessage = intl.formatMessage({
          id: 'EnquiryForm.contactInformationErrorMessage',
        });

        const header = (
          <div className={css.header}>
            {author ? <Avatar className={css.avatar} user={author} disableProfileLink /> : null}
            <div className={css.headerText}>
              <h2 className={css.heading}>
                <FormattedMessage
                  id={canEnquire ? 'EnquiryForm.heading' : 'EnquiryForm.headingCantEnquire'}
                  values={{ listingTitle }}
                />
              </h2>
              {canEnquire && enquiryPackage ? (
                <p className={css.packageLine}>
                  <FormattedMessage
                    id="EnquiryForm.aboutPackage"
                    values={{ title: enquiryPackage.title }}
                  />
                  <PricingModelBadge pricingModel={enquiryPackage.pricingModel} />
                  <PackagePrice
                    price={enquiryPackage.price}
                    pricingModel={enquiryPackage.pricingModel}
                  />
                </p>
              ) : null}
              {canEnquire ? (
                <p className={css.subtitle}>
                  <FormattedMessage
                    id="EnquiryForm.subtitle"
                    values={{
                      authorDisplayName,
                      b: chunks => <span className={css.recipient}>{chunks}</span>,
                    }}
                  />
                </p>
              ) : null}
            </div>
          </div>
        );

        if (!canEnquire) {
          return (
            <div className={classes}>
              {header}
              <p className={css.notice}>
                <FormattedMessage id="EnquiryForm.messageCantEnquire" />
              </p>
              <NamedLink name="ProfileSettingsPage" className={css.linkCantEnquire}>
                <FormattedMessage id="EnquiryForm.buttonCantEnquire" />
              </NamedLink>
            </div>
          );
        }

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {header}

            <div className={css.body}>
              <FieldMessageComposer
                id={fieldId}
                name="message"
                label={<FormattedMessage id="EnquiryForm.fieldLabel" />}
                placeholder={messagePlaceholder}
                message={message}
                stagedAttachments={attachments}
                onUploadAttachment={onUploadAttachment}
                onRemoveAttachment={onRemoveAttachment}
              />
              {showContactWarning ? (
                <MultiPurposeInfo
                  showWarning
                  warningMessage={contactInformationErrorMessage}
                  icon={<CiWarning />}
                />
              ) : (
                <p className={css.help}>
                  <FormattedMessage id="EnquiryForm.helpText" />
                </p>
              )}
            </div>

            <div className={css.footer}>
              {sendEnquiryError ? (
                <p className={css.error}>
                  <FormattedMessage id="EnquiryForm.sendEnquiryError" />
                </p>
              ) : null}
              <PrimaryButton
                className={classNames(css.submitButton, { [css.submitButtonLoading]: inProgress })}
                type="submit"
                inProgress={inProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="EnquiryForm.submitButtonText" />
              </PrimaryButton>
              <p className={css.terms}>
                <FormattedMessage id="EnquiryForm.termsNote" />{' '}
                <NamedLink name="TermsOfServicePage" className={css.termsLink}>
                  <FormattedMessage id="EnquiryForm.termsLink" />
                </NamedLink>
              </p>
            </div>
          </Form>
        );
      }}
    />
  );
};

EnquiryFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inProgress: false,
  sendEnquiryError: null,
  author: null,
  enquiryPackage: null,
  stagedAttachments: [],
  onUploadAttachment: null,
  onRemoveAttachment: null,
};

EnquiryFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  inProgress: bool,

  listingTitle: string.isRequired,
  authorDisplayName: string.isRequired,
  // The recipient; renders their avatar in the header when given.
  author: propTypes.user,
  // Package the client is asking about; renders a summary line under the heading.
  enquiryPackage: shape({
    title: string.isRequired,
    pricingModel: oneOf(PRICING_MODELS).isRequired,
    price: shape({ amount: number.isRequired, currency: string.isRequired }).isRequired,
  }),
  sendEnquiryError: propTypes.error,

  // Optional message attachments (see MessageAttachmentsPicker); the attach
  // control renders only when both handlers are given.
  stagedAttachments: array,
  onUploadAttachment: func,
  onRemoveAttachment: func,

  // from injectIntl
  intl: intlShape.isRequired,
};

const EnquiryForm = compose(injectIntl)(EnquiryFormComponent);

EnquiryForm.displayName = 'EnquiryForm';

export default EnquiryForm;
